import {
  Box,
  Button,
  MenuButton,
  MenuItem,
  Text,
  Badge,
  Stack,
  Flex,
  Icon,
} from '@chakra-ui/react'
import React, { useState } from 'react'
import { FiMoreHorizontal } from 'react-icons/fi'
import { GrTemplate } from 'react-icons/gr'
import { FiCheck, FiCopy, FiEye, FiEyeOff } from 'react-icons/fi'
import { templateIconsByInspectionType } from '../constants/templateIconsByInspectionType'
import { ITemplate } from '../interfaces/inspection-template'
import { MenuCustom } from './MenuCustom'
import Link from 'next/link'
import { chakra, Divider } from '@chakra-ui/react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react'

type Template = Pick<
  ITemplate,
  'name' | 'images' | 'usedCount' | 'inputs' | 'name' | 'inspectionType'
>

const SectionsList = ({ sections }: { sections: Template['sections'] }) => {
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const copyToClipboard = async (sectionId: string) => {
    try {
      await navigator.clipboard.writeText(sectionId)
      setCopiedId(sectionId)
      setTimeout(() => setCopiedId(null), 2000)
    } catch (err) {
      console.error('Failed to copy: ', err)
    }
  }

  return (
    <Stack spacing={3}>
      {sections.map((section) => (
        <Flex
          key={section.id}
          align='center'
          justify='space-between'
          p={3}
          borderRadius='lg'
          borderWidth='1px'
          borderColor='gray.200'
          bg='white'
        >
          <Box flex='1'>
            <Text fontSize='sm' fontWeight='medium'>
              {section.label}
            </Text>
            <Flex align='center' gap={2}>
              <Text
                isTruncated
                fontSize='xs'
                color='gray.500'
                fontFamily='mono'
                maxW='200px'
              >
                {section.identifier}
              </Text>
              <Button
                variant='ghost'
                size='sm'
                minW='6'
                h='6'
                p={0}
                onClick={() => copyToClipboard(section.identifier)}
                _hover={{ bg: 'gray.100' }}
              >
                {copiedId === section.identifier ? (
                  <Icon as={FiCheck} w={3} h={3} color='green.600' />
                ) : (
                  <Icon as={FiCopy} w={3} h={3} />
                )}
              </Button>
            </Flex>
          </Box>

          <Flex align='center' gap={2}>
            <Badge
              rounded='md'
              colorScheme={section.active ? 'blue' : 'gray'}
              fontSize='xs'
            >
              <Flex align='center' gap={1}>
                {section.active ? (
                  <Icon as={FiEye} w={3} h={3} />
                ) : (
                  <Icon as={FiEyeOff} w={3} h={3} />
                )}
                {section.active ? 'Visible' : 'Oculta'}
              </Flex>
            </Badge>
          </Flex>
        </Flex>
      ))}

      {!sections.length && (
        <Text textAlign='center' color='gray.500' fontSize='sm' py={4}>
          No hay secciones configuradas
        </Text>
      )}
    </Stack>
  )
}

export const TemplateCard = <T extends string>(props: {
  template: Pick<
    ITemplate,
    'name' | 'images' | 'usedCount' | 'inputs' | 'name' | 'inspectionType'
  >
  onClickMenuOption: (optId: T) => void
  menuOpts: {
    label: string
    id: T
    isDisabled?: boolean
    href?: string
  }[]
}) => {
  const InspectionTypeIcon =
    templateIconsByInspectionType[
      props.template
        .inspectionType as keyof typeof templateIconsByInspectionType
    ]

  const { isOpen, onOpen, onClose } = useDisclosure()

  return (
    <Box
      boxShadow={'lg'}
      borderRadius='lg'
      padding={'5'}
      borderColor={'gray.300'}
      borderWidth={'1px'}
      display='flex'
      flexDir={'column'}
      className='space-y-2'
      bg='white'
      height={'100%'}
      position='relative'
    >
      {props.template.type === 'built-in' && (
        <Box position='absolute' right={0} top={-8} p={5}>
          <Badge rounded='md' colorScheme='blue'>
            Global
          </Badge>
        </Box>
      )}

      <Box
        display={'flex'}
        justifyContent='space-between'
        alignItems={'center'}
        flexDir={'row'}
      >
        <Box
          className='space-x-5'
          display={'flex'}
          alignItems={'center'}
          flexDir={'row'}
        >
          <GrTemplate opacity={0.5} color='gray' fontSize={'1.5rem'} />
          {InspectionTypeIcon && (
            <InspectionTypeIcon width='30px' height='30px' />
          )}
        </Box>

        <MenuCustom
          menuListChildren={
            <>
              {props.menuOpts.map((ele) => {
                const itemContent = (
                  <MenuItem
                    isDisabled={ele.isDisabled}
                    onClick={() => props.onClickMenuOption(ele.id)}
                  >
                    {ele.label}
                  </MenuItem>
                )

                if (ele.href) return <Link href={ele.href}>{itemContent}</Link>

                return itemContent
              })}
            </>
          }
        >
          <MenuButton bg='transparent' as={Button}>
            <FiMoreHorizontal />
          </MenuButton>
        </MenuCustom>
      </Box>
      <Box>
        <Text fontSize={''} fontWeight={'bold'}>
          {props.template.name}{' '}
        </Text>

        <Text fontWeight='normal' color={'gray.500'} fontSize={'xs'}>
          #{props.template._id}
        </Text>

        <Box
          alignItems={'center'}
          display={'flex'}
          flexDir='row'
          className='space-x-2'
          justifyContent='space-between'
        >
          <Box className='space-x-2' display={'flex'} flexDir='row'>
            <Text>{props.template.images.length} imágenes</Text>
            <Text>{props.template.inputs.length} campos</Text>
            <Text>{props.template.usedCount} usos</Text>
          </Box>
        </Box>
        <Divider marginTop={2} marginBottom={2} orientation='horizontal' />
        <Stack direction='row' justifyContent='space-between'>
          <Box fontSize='sm' mb={4}>
            <Text fontWeight='bold'>
              {props.template.sections.length} secciones{' '}
              <chakra.span fontWeight='normal' color='gray.500'>
                (
                {
                  props.template.sections.filter((section) => section.active)
                    .length
                }{' '}
                visibles)
              </chakra.span>
            </Text>
          </Box>

          <Button size='xs' variant='outline' onClick={onOpen}>
            Ver secciones
          </Button>

          <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Secciones - {props.template.name}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <SectionsList sections={props.template.sections} />
              </ModalBody>
            </ModalContent>
          </Modal>
        </Stack>
      </Box>
    </Box>
  )
}
